import { useProducts } from '@airship-platform/airship-proposal';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../data-access/appointmentProposals';
import {
  useAppointmentProposalCosts,
  useAppointmentProposalSync,
  useMembershipData,
} from '../../data-offline';
import { collectOfflineSyncErrors } from '../../patterns';
import ROUTE_PATHS from '../../routes/paths';

const OfflineErrorAlert: React.FC = () => {
  const { lookupsError, lookupsSetError, lookupsSync, modifiersSync } =
    useProducts();
  const { costsCalculationError } = useAppointmentProposalCosts();
  const { pushSyncError } = useAppointmentProposalSync();
  const { membershipDataSync } = useMembershipData();
  const { options } = useAppointmentProposalOptions();
  const { currentProposal } = useAppointmentProposals();

  const errors = [
    lookupsError,
    lookupsSetError,
    lookupsSync.error,
    modifiersSync.error,
    membershipDataSync.error,
    costsCalculationError,
    pushSyncError,
  ].flatMap((e) => e || []);

  const rejected = collectOfflineSyncErrors([
    ...options.map((option) => ({
      label: option.name,
      errors: option.offlineSyncErrors,
    })),
    { label: 'This proposal', errors: currentProposal?.offlineSyncErrors },
  ]);

  if (!errors.length && !rejected.length) {
    return null;
  }

  return (
    <>
      {!!errors.length && (
        <Alert severity="error">
          Apologies, there was a problem syncing offline data. Please try again
          later. If the problem persists, please contact support.
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
          <Link to={ROUTE_PATHS.OFFLINE_DIAGNOSTICS}>Offline Diagnostics</Link>
        </Alert>
      )}
      {!!rejected.length && (
        <Alert severity="error">
          <Typography variant="subtitle2" sx={{ display: 'block' }}>
            {rejected.length === 1
              ? `A change couldn't be saved`
              : `${rejected.length} changes couldn't be saved`}
          </Typography>
          {rejected.map(({ label, summary }, index) => (
            <Typography key={index} variant="body2">
              {label}: {summary.details.join(', ')}
            </Typography>
          ))}
        </Alert>
      )}
    </>
  );
};

export default OfflineErrorAlert;
